import { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { toast } from 'react-toastify';

// comps
import { Box, Typography, Divider } from '@mui/material';
import DialogTemplate from './dialogTemplate';
import GlobalInput from '../global/input';
import GlobalDropdown from '../global/dropdown';
import GlobalButton from '../global/button';
import ToastMessage from '../global/toastMessage';

// icons
import { ReactComponent as TimeIcon } from '../../assets/icons/time-icon.svg';

// constants
import { colors } from '../../theme';
import { MOMENT_TIME_FORMAT, MOMENT_TIME_DISPLAY_FORMAT } from '../../constants/date_format';

const StudentApppintmentDialog = (props: any) => {
    const { dataItem, onClose, appointment, onCreate, onSave, roles } = props;
    const { timeIn, timeOut, topic, name, email, bcitId, role, id } = appointment;

    const [errorMsg, setErrorMsg] = useState(null);

    const [state, setState] = useState({
        role: null,
        name: '',
        email: '',
        bcitId: null,
        topic: null,
    });

    useEffect(() => {
        if (appointment.id) {
            setState({
                ...state,
                name,
                email,
                role,
                bcitId,
                topic,
            });
        }
    }, [appointment]);

    const isCreate = id === 0;

    const onChange = (name: any, value: any) => {
        setErrorMsg(null);
        setState({
            ...state,
            [name]: name === 'bcitId' ? value.toUpperCase() : value,
        });
    };

    // Validate form data
    const handleValidateData = (appointment) => {
        // Name, Email- if required fields are empty, send warning
        if (appointment.name === '') {
            setErrorMsg('Name is required.');
            return false;
        }
        if (appointment.email === '') {
            setErrorMsg('Email is required.');
            return false;
        }
        if (!appointment.role) {
            setErrorMsg('Selecting is a Role is required.');
            return false;
        }
        // BCIT ID - if character length does not equal 9
        if (appointment.bcitId && appointment.bcitId.length !== 9) {
            setErrorMsg('BCIT ID must be 9 characters.');
            return false;
        }
        // BCIT Email - if the email is for a student, must use my.bcit.ca email AND if there are extra spaces
        if (appointment.role?.name === 'BCIT Student' && !appointment.email.trim().endsWith('@my.bcit.ca')) {
            setErrorMsg('Student emails must be a @my.bcit.ca email.');
            return false;
        } else if (appointment.role?.name !== 'BCIT Student' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(appointment.email.trim())) {
            setErrorMsg('Email must be valid.');
            return false;
        }

        // Topic - If characters are over 100
        if (appointment.topic && appointment.topic.length > 100) {
            setErrorMsg('Topic must be less than 100 characters.');
            return false;
        }

        return true;
    };

    // Submit Form Data
    const handleSubmit = () => {
        const dataItem = {
            ...appointment,
            name: state.name,
            email: state.email,
            role: state.role,
            bcitId: state.bcitId,
            topic: state.topic,
        };

        const isValid = handleValidateData(dataItem);

        if (!isValid) return;
        if (isCreate) onCreate(dataItem);
        else onSave(dataItem);
    };

    const handleErrorState = (name: string) => {
        return errorMsg?.includes(name);
    };

    // limit character limit in textfield
    const bcitIdCharLimit = 9;
    const topicCharLimit = 100;

    const renderContent = () => {
        return (
            <Container>
                <FlexBox sx={{ width: '65%' }}>
                    <FlexBox>
                        <TimeIcon id="timeIcon" />
                        <div>
                            <Label variant="h6">Date</Label>
                            <Typography>
                                {dataItem.dayOfWeek}, {moment(dataItem.date).format('MMM DD YYYY')}
                            </Typography>
                        </div>
                    </FlexBox>
                    <div>
                        <Label variant="h6">Time In</Label>
                        <Typography>{moment(timeIn, MOMENT_TIME_FORMAT).format(MOMENT_TIME_DISPLAY_FORMAT)}</Typography>
                    </div>
                    <div>
                        <Label variant="h6">Time Out</Label>
                        <Typography>{moment(timeOut, MOMENT_TIME_FORMAT).format(MOMENT_TIME_DISPLAY_FORMAT)}</Typography>
                    </div>
                </FlexBox>
                <Divider sx={{ mt: '1em', mb: '1.5em' }} />
                {errorMsg && (
                    <FlexBox>
                        <ToastMessage isError msg={errorMsg} />
                    </FlexBox>
                )}
                <FlexBox>
                    <GlobalInput label="Student Name" isRequired isError={handleErrorState('Name')} placeholder="First, Last Name" name={'name'} value={state.name} onChange={onChange} />
                    <GlobalInput label="Email" isRequired isError={handleErrorState('Email')} placeholder="sample@my.bcit.ca" name={'email'} value={state.email} onChange={onChange} />
                </FlexBox>
                <FlexBox sx={{ m: '1em 0' }}>
                    <GlobalDropdown
                        label="Role"
                        data={roles}
                        isRequired
                        isError={handleErrorState('Role')}
                        placeholder={isCreate ? 'Select a role' : ''}
                        name={'name'}
                        field={'id'}
                        dataItemKey={'role'}
                        value={state.role}
                        onChange={onChange}
                    />
                    <GlobalInput
                        label="BCIT ID"
                        isError={handleErrorState('BCIT ID')}
                        placeholder="A01123456"
                        name={'bcitId'}
                        value={state.bcitId}
                        onChange={onChange}
                        inputProps={{
                            maxLength: bcitIdCharLimit,
                        }}
                    />
                </FlexBox>
                <GlobalInput
                    multiline
                    minRows={2}
                    label="Topic"
                    isError={handleErrorState('Topic')}
                    placeholder="Notes or topic of discussion (Max 100 characters)"
                    name={'topic'}
                    value={state.topic}
                    onChange={onChange}
                    inputProps={{
                        maxLength: topicCharLimit,
                    }}
                />
                <FlexBox sx={{ mt: '1em' }}>
                    <GlobalButton text="cancel" variant="outlined" onClick={onClose} />
                    <GlobalButton text={isCreate ? 'create' : 'save changes'} variant="contained" onClick={handleSubmit} />
                </FlexBox>
            </Container>
        );
    };
    return <DialogTemplate {...props} title={`${isCreate ? 'Create' : 'Edit'} an Office Hour Appointment`} renderContent={renderContent} />;
};

export default StudentApppintmentDialog;

const Container = styled(Box)`
    && {
        width: 100%;
        padding: 1em 0px;
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
        justify-content: space-between;

        #timeIcon {
            width: 30px;
            height: 30px;
            margin-right: 10px;
        }
    }
`;

const Label = styled(Typography)`
    && {
        color: ${colors.blue.main};
    }
`;
